import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/Item_attributes_SalvarAtributosCategoriaInput';
import '../contracts/Item_attributes_SalvarInput';
import '../contracts/Item_attributes_SalvarOpcaoInput';

import '../contracts/Item_attributes_AtributosCategoriaResponse';
import '../contracts/Item_attributes_ListaResponse';
import '../contracts/Item_attributes_OpcoesResponse';
import '../contracts/Item_attributes_RemoverOpcaoResponse';
import '../contracts/Item_attributes_RemoverResponse';
import '../contracts/Item_attributes_SalvarAtributosCategoriaResponse';
import '../contracts/Item_attributes_SalvarOpcaoResponse';
import '../contracts/Item_attributes_SalvarResponse';
import type { Item_attributes_SalvarAtributosCategoriaInput } from '../contracts/Item_attributes_SalvarAtributosCategoriaInput';
import type { Item_attributes_SalvarInput } from '../contracts/Item_attributes_SalvarInput';
import type { Item_attributes_SalvarOpcaoInput } from '../contracts/Item_attributes_SalvarOpcaoInput';
export function useSOItemAttributes() {
  const isLoading = ref(false);
  const error = ref(null);
  const basePath = '/index.php/so/item_attributes';

  const fetchAll = async () => {
    log('useSOItemAttributes', 'fetchAll', 'Fetching all item attributes');
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/lista`, { responseContract: 'Item_attributes_ListaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar atributos de item');
    } catch (err) {
      logError('useSOItemAttributes', 'fetchAll', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const save = async (data: Item_attributes_SalvarInput) => {
    log('useSOItemAttributes', 'save', 'Saving item attribute', data);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar`, data, { inputContract: 'Item_attributes_SalvarInput', responseContract: 'Item_attributes_SalvarResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar atributo de item');
    } catch (err) {
      logError('useSOItemAttributes', 'save', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const remove = async (id: any) => {
    log('useSOItemAttributes', 'remove', 'Removing item attribute', { id });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/remover/${id}`, null, { responseContract: 'Item_attributes_RemoverResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover atributo de item');
    } catch (err) {
      logError('useSOItemAttributes', 'remove', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchOptions = async (attributeId: number) => {
    log('useSOItemAttributes', 'fetchOptions', 'Fetching item attribute options', { attributeId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/opcoes/${attributeId}`, { responseContract: 'Item_attributes_OpcoesResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar opcoes do atributo de item');
    } catch (err) {
      logError('useSOItemAttributes', 'fetchOptions', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const saveOption = async (data: Item_attributes_SalvarOpcaoInput) => {
    log('useSOItemAttributes', 'saveOption', 'Saving item attribute option', data);
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar_opcao`, data, { inputContract: 'Item_attributes_SalvarOpcaoInput', responseContract: 'Item_attributes_SalvarOpcaoResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar opcao do atributo de item');
    } catch (err) {
      logError('useSOItemAttributes', 'saveOption', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const removeOption = async (optionId: number) => {
    log('useSOItemAttributes', 'removeOption', 'Removing item attribute option', { optionId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/remover_opcao/${optionId}`, null, { responseContract: 'Item_attributes_RemoverOpcaoResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao remover opcao do atributo de item');
    } catch (err) {
      logError('useSOItemAttributes', 'removeOption', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchCategoryAttributes = async (categoryId: number) => {
    log('useSOItemAttributes', 'fetchCategoryAttributes', 'Fetching category item attributes', { categoryId });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.get(`${basePath}/atributos_categoria/${categoryId}`, { responseContract: 'Item_attributes_AtributosCategoriaResponse' });
      if (response.success) {
        return response.dados || [];
      }
      throw new Error(response.error || 'Erro ao buscar atributos da categoria');
    } catch (err) {
      logError('useSOItemAttributes', 'fetchCategoryAttributes', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const saveCategoryAttributes = async (categoryId: any, attributes: any) => {
    const payload: Item_attributes_SalvarAtributosCategoriaInput = { attributes };
    log('useSOItemAttributes', 'saveCategoryAttributes', 'Saving category item attributes', {
      categoryId,
      payload
    });
    isLoading.value = true;
    error.value = null;
    try {
      const response = await httpService.post(`${basePath}/salvar_atributos_categoria/${categoryId}`, payload, { inputContract: 'Item_attributes_SalvarAtributosCategoriaInput', responseContract: 'Item_attributes_SalvarAtributosCategoriaResponse' });
      if (response.success) {
        return response;
      }
      throw new Error(response.error || 'Erro ao salvar atributos da categoria');
    } catch (err) {
      logError('useSOItemAttributes', 'saveCategoryAttributes', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    isLoading,
    error,
    fetchAll,
    save,
    remove,
    fetchOptions,
    saveOption,
    removeOption,
    fetchCategoryAttributes,
    saveCategoryAttributes
  };
}
